import { useEffect, useRef } from 'react';

const FLIP_DURATION = 5000; // card stays flipped 5 s
const INTERVAL = 2000;      // 2 s between each card flip start

/** Count how many grid children sit on the first visual row. */
function measureCardsPerRow(children: HTMLElement[]): number {
  if (children.length <= 1) return children.length || 1;
  const firstTop = children[0].getBoundingClientRect().top;
  for (let i = 1; i < children.length; i++) {
    if (Math.abs(children[i].getBoundingClientRect().top - firstTop) > 5) return i;
  }
  return children.length;
}

/**
 * Row-aware auto-flip for grid layouts with many cards.
 *
 * Each row is animated when it scrolls into view (30 % visible).
 * When the row scrolls out of view, timers are cancelled and cards reset.
 * Scrolling back re-triggers the animation — works in both directions.
 *
 *   Odd rows  (1st, 3rd, 5th …): flip 1st/3rd/5th cards → then 2nd/4th/6th
 *   Even rows (2nd, 4th, 6th …): flip 2nd/4th/6th cards → then 1st/3rd/5th
 *
 * Cards-per-row is measured from the actual DOM layout so it adapts to
 * desktop / tablet / mobile automatically.
 */
export function useAutoFlipCardsGrid<T extends number | string>(
  gridRef: React.RefObject<HTMLElement | null>,
  cardIds: T[],
  setFlipped: React.Dispatch<React.SetStateAction<Set<T>>>,
) {
  const rowTimers = useRef<Map<number, ReturnType<typeof setTimeout>[]>>(new Map());
  const activeRows = useRef<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  const cardIdsKey = cardIds.join(',');

  useEffect(() => {
    const gridEl = gridRef.current;
    if (!gridEl || cardIds.length === 0) return;

    const clearRowTimers = (ri: number) => {
      const t = rowTimers.current.get(ri);
      if (t) { t.forEach(clearTimeout); rowTimers.current.delete(ri); }
    };

    const scheduleRowFlip = (rowCards: T[], rowIndex: number) => {
      clearRowTimers(rowIndex);
      const timers: ReturnType<typeof setTimeout>[] = [];

      const group0 = rowCards.filter((_, i) => i % 2 === 0);
      const group1 = rowCards.filter((_, i) => i % 2 !== 0);

      const firstGroup  = rowIndex % 2 === 0 ? group0 : group1;
      const secondGroup = rowIndex % 2 === 0 ? group1 : group0;

      let offset = 0;

      firstGroup.forEach((id) => {
        timers.push(
          setTimeout(() => {
            setFlipped((prev) => { const n = new Set(prev); n.add(id); return n; });
          }, offset),
        );
        timers.push(
          setTimeout(() => {
            setFlipped((prev) => { const n = new Set(prev); n.delete(id); return n; });
          }, offset + FLIP_DURATION),
        );
        offset += INTERVAL;
      });

      secondGroup.forEach((id) => {
        timers.push(
          setTimeout(() => {
            setFlipped((prev) => { const n = new Set(prev); n.add(id); return n; });
          }, offset),
        );
        timers.push(
          setTimeout(() => {
            setFlipped((prev) => { const n = new Set(prev); n.delete(id); return n; });
          }, offset + FLIP_DURATION),
        );
        offset += INTERVAL;
      });

      rowTimers.current.set(rowIndex, timers);
    };

    const cancelRowFlip = (rowCards: T[], rowIndex: number) => {
      clearRowTimers(rowIndex);
      // Ensure all cards in the row are unflipped
      setFlipped((prev) => {
        const n = new Set(prev);
        rowCards.forEach((id) => n.delete(id));
        return n;
      });
    };

    // Wait one frame so the browser has finished layout
    const rafId = requestAnimationFrame(() => {
      const children = Array.from(gridEl.children) as HTMLElement[];
      if (children.length === 0) return;

      const cardsPerRow = measureCardsPerRow(children);

      const rows: T[][] = [];
      for (let i = 0; i < cardIds.length; i += cardsPerRow) {
        rows.push(cardIds.slice(i, i + cardsPerRow));
      }

      const elToRow = new Map<Element, number>();

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const ri = elToRow.get(entry.target);
            if (ri === undefined) return;

            if (entry.isIntersecting) {
              if (!activeRows.current.has(ri)) {
                activeRows.current.add(ri);
                scheduleRowFlip(rows[ri], ri);
              }
            } else {
              if (activeRows.current.has(ri)) {
                activeRows.current.delete(ri);
                cancelRowFlip(rows[ri], ri);
              }
            }
          });
        },
        { threshold: 0.3 },
      );

      // Observe the first card of every row (keep observing for re-entry)
      rows.forEach((_, ri) => {
        const el = children[ri * cardsPerRow];
        if (el) {
          elToRow.set(el, ri);
          observer.observe(el);
        }
      });

      observerRef.current = observer;
    });

    return () => {
      cancelAnimationFrame(rafId);
      observerRef.current?.disconnect();
      observerRef.current = null;
      rowTimers.current.forEach((t) => t.forEach(clearTimeout));
      rowTimers.current.clear();
      activeRows.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridRef, cardIdsKey, setFlipped]);
}
